import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Col,
  Row,
  Form,
  FloatingLabel,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import {
  faPhone,
  faMapPin,
  faHospital,
  faIdCard,
  faFileAlt,
  faEdit,
  faSave,
  faTimes,
  faDownload,
  faUser,
  faEye,
  faMailBulk,
  faMailForward
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AvatarUpload from 'components/common/AvatarUpload';
import classNames from 'classnames';
import team15 from 'assets/img/team/avatar-rounded.webp';
import {
  getProfile,
  updateProfile,
  downloadDocument,
  viewDocument
} from 'features/profile/profileApi';

const PharmacistProfilePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocViewer, setShowDocViewer] = useState(false);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getProfile();
        setProfileData(data);
        setFormData(data.profile);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const result = await updateProfile(formData);
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      setProfileData(result);
      setFormData(result.profile);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData(profileData?.profile || {});
    setIsEditing(false);
    setError(null);
  };

  // Handle document download
  const handleDocumentDownload = async (document: any) => {
    try {
      console.log('Starting download for:', document.document_name);
      await downloadDocument(document.document_name);
      console.log('Download successful for:', document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to download document';
      console.error('Download error:', errorMsg);
      setError(errorMsg);
    }
  };

  // Handle document view
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!profileData) {
    return <Alert variant="danger">Failed to load profile</Alert>;
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center g-4">
            <Col xs={12} sm="auto">
              <AvatarUpload size="5xl" src={team15} />
            </Col>
            <Col xs={12} sm>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h2 className="mb-1">{profileData.user.name}</h2>
                  <p className="text-body-secondary mb-2">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Pharmacist
                  </p>
                  <p className="text-body-secondary mb-0">
                    <FontAwesomeIcon icon={faMailBulk} className="me-2" />
                    <a
                      href={`mailto:${profileData.user.email}`}
                      className="text-decoration-none"
                    >
                      {profileData.user.email}
                    </a>
                  </p>
                </div>
                <div>
                  {!isEditing ? (
                    <Button
                      variant="phoenix-primary"
                      onClick={() => setIsEditing(true)}
                      startIcon={<FontAwesomeIcon icon={faEdit} />}
                      size="sm"
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        onClick={handleSave}
                        loading={isSaving}
                        startIcon={<FontAwesomeIcon icon={faSave} />}
                        size="sm"
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancel}
                        startIcon={<FontAwesomeIcon icon={faTimes} />}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Pharmacy Details Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faHospital} className="me-2 text-primary" />
            Pharmacy Information
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel controlId="pharmacy_name" label="Pharmacy Name">
                  <Form.Control
                    type="text"
                    name="pharmacy_name"
                    value={formData.pharmacy_name || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Pharmacy Name"
                  />
                </FloatingLabel>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel
                  controlId="contact_person"
                  label="Contact Person"
                >
                  <Form.Control
                    type="text"
                    name="contact_person"
                    value={formData.contact_person || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Contact Person"
                  />
                </FloatingLabel>
              </div>
            </Col>
            <Col md={6}>
              <div className="form-icon-container">
                <FloatingLabel
                  controlId="license_number"
                  label="License Number"
                >
                  <Form.Control
                    type="text"
                    name="license_number"
                    value={formData.license_number || ''}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="License Number"
                  />
                </FloatingLabel>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Compliance & Certifications Card */}
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
            Compliance Status
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <div className="alert alert-info mb-0" role="alert">
                <strong>License Number:</strong>{' '}
                {profileData.profile.license_number || 'Not provided'}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Documents Card */}
      <Card>
        <Card.Header className="bg-light">
          <Card.Title className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
            Required Documents ({profileData.documents.length})
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {profileData.documents.length > 0 ? (
            <div className="list-group">
              {profileData.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div className="flex-grow-1">
                    <h6 className="mb-1 d-flex align-items-center">
                      <span
                        className={classNames(
                          'badge me-2',
                          doc.doc_type === 'gphc_number_doc' ||
                            doc.doc_type === 'smartcard'
                            ? 'bg-success'
                            : 'bg-info'
                        )}
                      >
                        {doc.doc_type === 'gphc_number_doc'
                          ? 'GPhC'
                          : doc.doc_type === 'smartcard'
                          ? 'SMARTCARD'
                          : 'CERT'}
                      </span>
                      {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                    </h6>
                    <small className="text-body-secondary">
                      {doc.document_name}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={() => handleDocumentView(doc)}
                    >
                      View
                    </Button>
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      startIcon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={() => handleDocumentDownload(doc)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              No documents uploaded yet
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Document Viewer Modal */}
      <Modal
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.doc_type.replace(/_/g, ' ').toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="text-center">
              <p className="mb-3 text-body-secondary">
                {selectedDocument.document_name}
              </p>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileAlt}
                  size="3x"
                  className="text-muted mb-3"
                />
                <p className="text-muted mb-2">
                  <strong>{selectedDocument.document_name}</strong>
                </p>
                <p className="text-muted small mb-4">
                  Click the button below to view or download this document.
                </p>
                <Button
                  variant="phoenix-primary"
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => {
                    handleDocumentDownload(selectedDocument);
                  }}
                >
                  Open Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PharmacistProfilePage;
